import { useQuery } from '@apollo/client/react';
import { Loader, Notice, Stack, Text, TextField } from 'braid-design-system';
import { useState } from 'react';
import { useDebounce } from 'use-debounce';
import { QuestionnaireForm } from 'wingman-fe';

import type {
  ApplicationQuestionnaireQuery,
  ApplicationQuestionnaireQueryVariables,
} from '#src/types/generated/graphql.ts';

import { APPLICATION_QUESTIONNAIRE } from './queries.ts';
import { SectionHeadingLink } from './SectionHeadingLink/SectionHeadingLink.tsx';

interface QuestionnaireLookupProps {
  id: string;
}

const QuestionnaireLookup = ({ id }: QuestionnaireLookupProps) => {
  const { data, error, loading } = useQuery<
    ApplicationQuestionnaireQuery,
    ApplicationQuestionnaireQueryVariables
  >(APPLICATION_QUESTIONNAIRE, {
    variables: {
      id,
    },
  });

  if (data?.applicationQuestionnaire) {
    return (
      <QuestionnaireForm
        components={data.applicationQuestionnaire.components}
        type="ApplicationQuestionnaire"
      />
    );
  }

  if (loading) {
    return <Loader />;
  }

  return (
    <Notice tone={error ? 'critical' : 'info'}>
      <Text>{error?.message ?? 'Hmm, we can’t find that questionnaire.'}</Text>
    </Notice>
  );
};

export const QuestionnaireLookupSection = () => {
  const [oid, setOid] = useState('');

  const [debouncedOid] = useDebounce(oid.trim(), 500);

  return (
    <Stack space="large">
      <SectionHeadingLink
        href="/use-cases/job-posting/questionnaires"
        id="seek-questionnaire-lookup-heading"
      >
        Questionnaire lookup
      </SectionHeadingLink>

      <TextField
        aria-label="Questionnaire ID"
        id="seek-questionnaire-lookup-id-input"
        onChange={(event) => setOid(event.currentTarget.value)}
        value={oid}
      />

      {debouncedOid ? <QuestionnaireLookup id={debouncedOid} /> : null}
    </Stack>
  );
};
